/**
 * Module Bibliothèque GéCéram (bible §8) — contrat partagé.
 */
import { z } from "zod";
import type { Permission, Role } from "./roles";

const Uuid = z.string().uuid();
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Les 12 catégories initiales (§8.2) — taxonomie administrable en V1.1. */
export const CATEGORIES_DOCUMENT = [
  "tarifs",
  "guides_prescription",
  "empreintes_numeriques",
  "materiaux_protocoles",
  "conjointe_ceramique",
  "adjointe",
  "implantologie",
  "logistique",
  "sav_retours",
  "modeles_courriers",
  "administratif_finance",
  "procedures_internes",
] as const;
export type CategorieDocument = (typeof CATEGORIES_DOCUMENT)[number];

export const CATEGORIE_DOCUMENT_LABELS: Record<CategorieDocument, string> = {
  tarifs: "Tarifs et conditions commerciales",
  guides_prescription: "Guides de prescription",
  empreintes_numeriques: "Empreintes, scans et fichiers numériques",
  materiaux_protocoles: "Matériaux et protocoles techniques",
  conjointe_ceramique: "Prothèse conjointe et céramique",
  adjointe: "Prothèse adjointe",
  implantologie: "Implantologie et chirurgie guidée",
  logistique: "Logistique, enlèvements et livraisons",
  sav_retours: "SAV, retours et refontes",
  modeles_courriers: "Modèles de courriers et communications",
  administratif_finance: "Administratif, finance et conformité",
  procedures_internes: "Procédures internes, qualité et formation",
};

/** Workflow §8.6 : Brouillon → En validation → Validé → Archivé. */
export const STATUTS_DOCUMENT = ["brouillon", "en_validation", "valide", "archive"] as const;
export type StatutDocument = (typeof STATUTS_DOCUMENT)[number];

export const STATUT_DOCUMENT_LABELS: Record<StatutDocument, string> = {
  brouillon: "Brouillon",
  en_validation: "En validation",
  valide: "Validé",
  archive: "Archivé",
};

export const DocumentVersionSchema = z.object({
  id: Uuid,
  numero: z.number().int().min(1),
  nomFichier: z.string(),
  mime: z.string(),
  taille: z.number().int().min(0),
  sha256: z.string(),
  analyse: z.enum(["basique_ok", "quarantaine"]),
  commentaire: z.string().nullable(),
  auteur: z.string(),
  creeLe: z.string().datetime({ offset: true }),
});
export type DocumentVersion = z.infer<typeof DocumentVersionSchema>;

export const DocumentSchema = z.object({
  id: Uuid,
  titre: z.string(),
  description: z.string().nullable(),
  categorie: z.enum(CATEGORIES_DOCUMENT),
  tags: z.array(z.string()),
  statut: z.enum(STATUTS_DOCUMENT),
  audience: z.enum(["interne", "praticiens"]),
  proprietaire: z.string(),
  dateEffet: IsoDate.nullable(),
  dateRevision: IsoDate.nullable(),
  /** placeholder « Contenu à fournir » (§8.3) — jamais partageable. */
  placeholder: z.boolean(),
  /** tarif : version en vigueur = dernier Validé dont la date d'effet est passée (§8.5). */
  enVigueur: z.boolean(),
  versions: z.array(DocumentVersionSchema),
  remplaceId: Uuid.nullable(),
});
export type Document = z.infer<typeof DocumentSchema>;

export const BibliothequeResponseSchema = z.object({
  items: z.array(DocumentSchema),
  total: z.number().int().min(0),
});
export type BibliothequeResponse = z.infer<typeof BibliothequeResponseSchema>;

/* ------------------------------------------------------------------- droits */

/** §8.6 : Dove publie/archive ; Estelle si permission « publication » accordée. */
export function canPublierDocument(roles: readonly Role[], permissions: readonly Permission[]): boolean {
  return roles.includes("admin") || (permissions as readonly string[]).includes("publication");
}

/** §8.6 : le comptable valide les documents financiers qui lui sont attribués. */
export function canValiderDocument(
  roles: readonly Role[],
  permissions: readonly Permission[],
  categorie: CategorieDocument,
): boolean {
  if (canPublierDocument(roles, permissions)) return true;
  return categorie === "administratif_finance" && roles.includes("comptabilite");
}

/** §8.6 : Élodie (et le staff) propose et modifie les brouillons. */
export function canProposerDocument(roles: readonly Role[]): boolean {
  return roles.some((r) => ["admin", "secretariat", "commercial", "production", "comptabilite", "logistique"].includes(r));
}
